import type { EventOrderFormField } from '../hooks/useEvents';

export type RegistrantEditAttendee = {
  full_name: string;
  email: string;
  phone: string;
};

export type RegistrantAttendeeResponse = {
  attendee_index: number;
  attendee_name: string;
  responses: Record<string, unknown>;
};

export type RegistrantEditState = {
  fullName: string;
  email: string;
  phone: string;
  notes: string;
  quantity: number;
  attendees: RegistrantEditAttendee[];
  orderLevelValues: Record<string, unknown>;
  attendeeResponses: RegistrantAttendeeResponse[];
};

export const MAX_REGISTRANT_EDIT_QUANTITY = 10;

export type RegistrationForEdit = {
  full_name: string;
  email: string;
  phone: string | null;
  notes: string | null;
  quantity: number;
  form_response: Record<string, unknown>;
};

const STRUCTURAL_FORM_RESPONSE_KEYS = new Set([
  'registration_type',
  'registrationtype',
  'primary_contact',
  'primarycontact',
  'attendee_responses',
  'attendeeresponses',
  'group_members',
  'groupmembers',
  'group_mode',
  'groupmode',
  'group_join',
  'groupjoin',
  'group_id',
  'groupid',
  'group_name',
  'groupname',
  'initial_payment_selection',
  'initialpaymentselection',
  '_mobile_checkout',
  'mobile_checkout',
]);

const isInternalFormResponseKey = (key: string) => {
  const normalized = String(key || '').trim();
  if (!normalized) return true;
  if (normalized.startsWith('_')) return true;
  return isStructuralFormResponseKey(normalized);
};

const DEFAULT_FIELD_KEYS_SHOWN_IN_PRIMARY = new Set(['email', 'phone']);

export type CustomFieldInputValue = string | boolean | string[];

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

export const isStructuralFormResponseKey = (key: string) =>
  STRUCTURAL_FORM_RESPONSE_KEYS.has(String(key || '').trim().toLowerCase());

export const isHiddenFormResponseKey = (key: string) => isInternalFormResponseKey(key);

export const isCollectPerAttendeeField = (field: Pick<EventOrderFormField, 'validation'>) =>
  Boolean(
    field.validation &&
      typeof field.validation === 'object' &&
      !Array.isArray(field.validation) &&
      (field.validation as Record<string, unknown>).collect_per_attendee,
  );

const pickString = (row: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = String(row[key] || '').trim();
    if (value) return value;
  }
  return '';
};

const defaultCustomValueForField = (fieldType: EventOrderFormField['field_type']): CustomFieldInputValue => {
  if (fieldType === 'multi_select') return [];
  if (fieldType === 'checkbox') return false;
  return '';
};

export const normalizeCustomFieldSubmissionValue = (
  field: Pick<EventOrderFormField, 'field_type'>,
  rawValue: CustomFieldInputValue | undefined,
): CustomFieldInputValue => {
  if (field.field_type === 'checkbox') {
    return Boolean(rawValue);
  }
  if (field.field_type === 'multi_select') {
    if (Array.isArray(rawValue)) {
      return rawValue.map((item) => String(item || '').trim()).filter(Boolean);
    }
    if (typeof rawValue === 'string' && rawValue.trim()) {
      return [rawValue.trim()];
    }
    return [];
  }

  const text = String(rawValue ?? '').trim();
  if (field.field_type === 'number') {
    if (!text) return '';
    const asNumber = Number(text);
    return Number.isFinite(asNumber) ? String(asNumber) : text;
  }
  return text;
};

export const formatCheckboxResponse = (value: unknown) => (value === true ? 'Yes' : 'No');

export const isCustomFieldValueEmpty = (
  field: Pick<EventOrderFormField, 'field_type'>,
  value: CustomFieldInputValue,
): boolean => {
  if (field.field_type === 'checkbox') {
    return value !== true;
  }
  if (field.field_type === 'multi_select') {
    return !Array.isArray(value) || value.length === 0;
  }
  return !String(value || '').trim();
};

export const parseFieldOptions = (options: unknown): string[] => {
  if (Array.isArray(options)) {
    return options.map((item) => String(item || '').trim()).filter(Boolean);
  }
  if (options && typeof options === 'object') {
    const values = (options as { values?: unknown }).values;
    if (Array.isArray(values)) {
      return values.map((item) => String(item || '').trim()).filter(Boolean);
    }
  }
  return [];
};

export const formatCustomFieldLabel = (fieldKey: string) =>
  fieldKey
    .split(/[_-]+/)
    .map((segment) => segment.trim())
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

const inferFieldTypeFromValue = (value: unknown): EventOrderFormField['field_type'] => {
  if (typeof value === 'boolean') return 'checkbox';
  if (typeof value === 'number') return 'number';
  if (Array.isArray(value)) return 'multi_select';
  return 'short_text';
};

const buildSyntheticFormField = (fieldKey: string, value: unknown): EventOrderFormField => ({
  field_key: fieldKey,
  label: formatCustomFieldLabel(fieldKey),
  field_type: inferFieldTypeFromValue(value),
  is_required: false,
  sort_order: 9999,
  options: Array.isArray(value) ? value.map((item) => String(item || '').trim()).filter(Boolean) : [],
  validation: {},
  placeholder: '',
  help_text: '',
});

export const resolveOrderLevelEditableFields = (
  formFields: EventOrderFormField[],
  formResponse: Record<string, unknown>,
): EventOrderFormField[] => {
  const responseRecord = asRecord(formResponse);
  const resolved = new Map<string, EventOrderFormField>();

  for (const field of formFields) {
    if (isInternalFormResponseKey(field.field_key)) continue;
    if (isCollectPerAttendeeField(field)) continue;
    if (DEFAULT_FIELD_KEYS_SHOWN_IN_PRIMARY.has(field.field_key)) continue;
    resolved.set(field.field_key, field);
  }

  for (const [key, value] of Object.entries(responseRecord)) {
    if (isInternalFormResponseKey(key)) continue;
    if (DEFAULT_FIELD_KEYS_SHOWN_IN_PRIMARY.has(key)) continue;
    if (resolved.has(key)) continue;
    const configuredField = formFields.find((field) => field.field_key === key);
    if (configuredField && isCollectPerAttendeeField(configuredField)) continue;
    if (value === null || value === undefined || value === '') continue;
    resolved.set(key, buildSyntheticFormField(key, value));
  }

  const configured = formFields.filter(
    (field) => resolved.has(field.field_key) && field.sort_order < 9999,
  );
  const synthetic = Array.from(resolved.values()).filter((field) => field.sort_order >= 9999);
  return [...configured, ...synthetic];
};

export const resolvePerAttendeeEditableFields = (
  formFields: EventOrderFormField[],
  attendeeResponses: RegistrantAttendeeResponse[],
): EventOrderFormField[] => {
  const resolved = new Map<string, EventOrderFormField>();

  for (const field of formFields) {
    if (isInternalFormResponseKey(field.field_key)) continue;
    if (!isCollectPerAttendeeField(field)) continue;
    resolved.set(field.field_key, field);
  }

  for (const entry of attendeeResponses) {
    for (const [key, value] of Object.entries(entry.responses || {})) {
      if (isInternalFormResponseKey(key)) continue;
      if (resolved.has(key)) continue;
      if (value === null || value === undefined || value === '') continue;
      resolved.set(key, buildSyntheticFormField(key, value));
    }
  }

  const configured = formFields.filter(
    (field) => resolved.has(field.field_key) && field.sort_order < 9999,
  );
  const synthetic = Array.from(resolved.values()).filter((field) => field.sort_order >= 9999);
  return [...configured, ...synthetic];
};

export const shouldShowOrderLevelFieldInEditForm = (field: EventOrderFormField) => {
  if (isInternalFormResponseKey(field.field_key)) return false;
  if (DEFAULT_FIELD_KEYS_SHOWN_IN_PRIMARY.has(field.field_key)) return false;
  if (isCollectPerAttendeeField(field)) return false;
  return true;
};

export const getOrderLevelCustomFields = (fields: EventOrderFormField[]) =>
  fields.filter(shouldShowOrderLevelFieldInEditForm);

export const getPerAttendeeCustomFields = (fields: EventOrderFormField[]) =>
  fields.filter((field) => isCollectPerAttendeeField(field) && !isInternalFormResponseKey(field.field_key));

const serializeObjectFieldValue = (value: Record<string, unknown>) => {
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
};

const readCustomFieldValue = (
  field: EventOrderFormField,
  rawValue: unknown,
): CustomFieldInputValue => {
  if (field.field_type === 'checkbox') {
    return Boolean(rawValue);
  }
  if (field.field_type === 'multi_select') {
    if (Array.isArray(rawValue)) {
      return rawValue
        .map((item) => {
          if (item && typeof item === 'object') return serializeObjectFieldValue(item as Record<string, unknown>);
          return String(item || '').trim();
        })
        .filter(Boolean);
    }
    if (typeof rawValue === 'string' && rawValue.trim()) {
      return [rawValue.trim()];
    }
    return [];
  }
  if (rawValue && typeof rawValue === 'object') {
    if (Array.isArray(rawValue)) {
      return rawValue
        .map((item) => (item && typeof item === 'object' ? serializeObjectFieldValue(item as Record<string, unknown>) : String(item || '').trim()))
        .filter(Boolean)
        .join(', ');
    }
    return serializeObjectFieldValue(rawValue as Record<string, unknown>);
  }
  return String(rawValue ?? '').trim();
};

export const parseRegistrationEditState = (
  registration: RegistrationForEdit,
  formFields: EventOrderFormField[],
): RegistrantEditState => {
  const responseRecord = asRecord(registration.form_response);
  const quantity = Math.max(1, Number(registration.quantity || 1));

  const attendees: RegistrantEditAttendee[] = [
    {
      full_name: String(registration.full_name || '').trim(),
      email: String(registration.email || '').trim(),
      phone: String(registration.phone || '').trim(),
    },
  ];

  const groupMembersRaw = Array.isArray(responseRecord.group_members)
    ? (responseRecord.group_members as Record<string, unknown>[])
    : [];
  for (const member of groupMembersRaw) {
    attendees.push({
      full_name: pickString(member, ['full_name', 'fullName', 'name', 'attendee_name', 'attendeeName']),
      email: pickString(member, ['email', 'email_address', 'emailAddress']),
      phone: pickString(member, ['phone', 'phone_number', 'phoneNumber', 'mobile', 'mobile_number']),
    });
  }

  const normalizedAttendees = attendees.slice(0, quantity);
  while (normalizedAttendees.length < quantity) {
    normalizedAttendees.push({ full_name: '', email: '', phone: '' });
  }

  const orderLevelFields = resolveOrderLevelEditableFields(formFields, responseRecord);
  const orderLevelValues: Record<string, unknown> = {};
  for (const field of orderLevelFields) {
    if (Object.prototype.hasOwnProperty.call(responseRecord, field.field_key)) {
      orderLevelValues[field.field_key] = readCustomFieldValue(field, responseRecord[field.field_key]);
    } else {
      orderLevelValues[field.field_key] = defaultCustomValueForField(field.field_type);
    }
  }

  const perAttendeeFields = getPerAttendeeCustomFields(formFields);
  const responseRowsRaw = Array.isArray(responseRecord.attendee_responses)
    ? (responseRecord.attendee_responses as Record<string, unknown>[])
    : [];
  const attendeeResponses: RegistrantAttendeeResponse[] = normalizedAttendees.map((attendee, attendeeIndex) => {
    const existingEntry = responseRowsRaw.find((entry) => Number(entry.attendee_index) === attendeeIndex) || responseRowsRaw[attendeeIndex];
    const existingResponses = asRecord(existingEntry?.responses);
    const responses: Record<string, unknown> = {};
    for (const field of perAttendeeFields) {
      responses[field.field_key] = Object.prototype.hasOwnProperty.call(existingResponses, field.field_key)
        ? readCustomFieldValue(field, existingResponses[field.field_key])
        : defaultCustomValueForField(field.field_type);
    }
    for (const [key, value] of Object.entries(existingResponses)) {
      if (Object.prototype.hasOwnProperty.call(responses, key)) continue;
      if (isInternalFormResponseKey(key)) continue;
      responses[key] = value;
    }
    return {
      attendee_index: attendeeIndex,
      attendee_name: attendee.full_name || `Attendee ${attendeeIndex + 1}`,
      responses,
    };
  });
  const resolvedPerAttendeeFields = resolvePerAttendeeEditableFields(formFields, attendeeResponses);
  for (const entry of attendeeResponses) {
    for (const field of resolvedPerAttendeeFields) {
      if (Object.prototype.hasOwnProperty.call(entry.responses, field.field_key)) continue;
      entry.responses[field.field_key] = defaultCustomValueForField(field.field_type);
    }
  }

  hydrateOrderLevelFieldValues(orderLevelValues, orderLevelFields, registration, responseRecord);

  const composedFullName = composeFullNameFromEditState({
    fullName: normalizedAttendees[0]?.full_name || '',
    email: normalizedAttendees[0]?.email || '',
    phone: normalizedAttendees[0]?.phone || '',
    notes: String(registration.notes || '').trim(),
    quantity,
    attendees: normalizedAttendees,
    orderLevelValues,
    attendeeResponses,
  });
  if (composedFullName && normalizedAttendees[0]) {
    normalizedAttendees[0] = { ...normalizedAttendees[0], full_name: composedFullName };
  }

  return {
    fullName: composedFullName || normalizedAttendees[0]?.full_name || '',
    email: normalizedAttendees[0]?.email || '',
    phone: normalizedAttendees[0]?.phone || '',
    notes: String(registration.notes || '').trim(),
    quantity,
    attendees: normalizedAttendees,
    orderLevelValues,
    attendeeResponses,
  };
};

const splitFullName = (fullName: string) => {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first_name: '', last_name: '' };
  if (parts.length === 1) return { first_name: parts[0], last_name: '' };
  return { first_name: parts[0], last_name: parts.slice(1).join(' ') };
};

export const composeFullNameFromEditState = (state: RegistrantEditState): string => {
  const first = String(state.orderLevelValues.first_name || '').trim();
  const last = String(state.orderLevelValues.last_name || '').trim();
  const composed = `${first} ${last}`.trim();
  if (composed) return composed;
  return String(state.fullName || '').trim();
};

const resolveSingleOrGroupValue = (responseRecord: Record<string, unknown>) => {
  const explicit = String(responseRecord.single_or_group || '').trim();
  if (explicit) return explicit;
  const registrationType = String(responseRecord.registration_type || '').trim().toLowerCase();
  if (registrationType === 'group') return 'Group';
  if (registrationType === 'single') return 'Single';
  const groupMembers = Array.isArray(responseRecord.group_members) ? responseRecord.group_members : [];
  if (groupMembers.length > 0) return 'Group';
  return 'Single';
};

const hydrateOrderLevelFieldValues = (
  orderLevelValues: Record<string, unknown>,
  orderLevelFields: EventOrderFormField[],
  registration: RegistrationForEdit,
  responseRecord: Record<string, unknown>,
) => {
  const primaryContact = asRecord(responseRecord.primary_contact);
  const resolvedFullName = String(registration.full_name || primaryContact.full_name || '').trim();
  const nameParts = splitFullName(resolvedFullName);
  const fieldKeys = new Set(orderLevelFields.map((field) => field.field_key));

  if (fieldKeys.has('first_name') && !String(orderLevelValues.first_name || '').trim()) {
    orderLevelValues.first_name = String(responseRecord.first_name || nameParts.first_name || '').trim();
  }
  if (fieldKeys.has('last_name') && !String(orderLevelValues.last_name || '').trim()) {
    orderLevelValues.last_name = String(responseRecord.last_name || nameParts.last_name || '').trim();
  }
  if (fieldKeys.has('single_or_group') && !String(orderLevelValues.single_or_group || '').trim()) {
    orderLevelValues.single_or_group = resolveSingleOrGroupValue(responseRecord);
  }
};

export const buildFormResponsePayload = (args: {
  state: RegistrantEditState;
  existingFormResponse: Record<string, unknown>;
  formFields: EventOrderFormField[];
  registrationType?: string | null;
}): Record<string, unknown> => {
  const existing = asRecord(args.existingFormResponse);
  const composedFullName = composeFullNameFromEditState(args.state);
  const primary = args.state.attendees[0] || {
    full_name: composedFullName,
    email: args.state.email,
    phone: args.state.phone,
  };

  const merged: Record<string, unknown> = { ...existing };
  const preservedKeys = [
    'group_mode',
    'group_join',
    'group_id',
    'group_name',
    'initial_payment_selection',
  ] as const;
  for (const key of preservedKeys) {
    if (Object.prototype.hasOwnProperty.call(existing, key)) {
      merged[key] = existing[key];
    }
  }

  merged.primary_contact = {
    full_name: composedFullName || primary.full_name,
    email: (primary.email || args.state.email).trim().toLowerCase(),
    phone: primary.phone || args.state.phone || null,
  };
  merged.group_members = args.state.attendees.slice(1).map((attendee) => ({
    full_name: attendee.full_name,
    email: attendee.email.trim().toLowerCase(),
    phone: attendee.phone || null,
  }));

  const normalizedAttendeeResponses = args.state.attendeeResponses.map((entry, index) => ({
    attendee_index: index,
    attendee_name: args.state.attendees[index]?.full_name || entry.attendee_name || `Attendee ${index + 1}`,
    responses: Object.fromEntries(
      Object.entries(entry.responses).map(([key, value]) => {
        const field = args.formFields.find((item) => item.field_key === key);
        if (!field) return [key, value];
        return [key, normalizeCustomFieldSubmissionValue(field, value as CustomFieldInputValue)];
      }),
    ),
  }));
  merged.attendee_responses = normalizedAttendeeResponses;

  for (const field of resolveOrderLevelEditableFields(args.formFields, args.state.orderLevelValues)) {
    const rawValue = args.state.orderLevelValues[field.field_key] as CustomFieldInputValue | undefined;
    const normalized = normalizeCustomFieldSubmissionValue(field, rawValue);
    if (!isCustomFieldValueEmpty(field, normalized)) {
      merged[field.field_key] = normalized;
    } else {
      delete merged[field.field_key];
    }
  }

  for (const [key, value] of Object.entries(args.state.orderLevelValues)) {
    if (isInternalFormResponseKey(key)) continue;
    if (args.formFields.some((field) => field.field_key === key && isCollectPerAttendeeField(field))) continue;
    if (resolveOrderLevelEditableFields(args.formFields, args.state.orderLevelValues).some((field) => field.field_key === key)) {
      continue;
    }
    merged[key] = value;
  }

  const hasFirstName = args.formFields.some((field) => field.field_key === 'first_name')
    || Object.prototype.hasOwnProperty.call(args.state.orderLevelValues, 'first_name');
  const hasLastName = args.formFields.some((field) => field.field_key === 'last_name')
    || Object.prototype.hasOwnProperty.call(args.state.orderLevelValues, 'last_name');
  if (hasFirstName || hasLastName) {
    const firstFromState = String(args.state.orderLevelValues.first_name || '').trim();
    const lastFromState = String(args.state.orderLevelValues.last_name || '').trim();
    const split = splitFullName(composedFullName);
    if (hasFirstName) merged.first_name = firstFromState || split.first_name;
    if (hasLastName) merged.last_name = lastFromState || split.last_name;
  }

  const singleOrGroup = String(args.state.orderLevelValues.single_or_group || '').trim();
  if (singleOrGroup) {
    merged.single_or_group = singleOrGroup;
    merged.registration_type = singleOrGroup.toLowerCase() === 'group' ? 'group' : 'single';
  } else if (args.registrationType) {
    merged.registration_type = args.registrationType;
  } else if (Object.prototype.hasOwnProperty.call(existing, 'registration_type')) {
    merged.registration_type = existing.registration_type;
  }

  return merged;
};

export const validateRegistrantEditState = (args: {
  state: RegistrantEditState;
  formFields: EventOrderFormField[];
  checkedInAttendeeIndexes: number[];
  previousQuantity: number;
}): string | null => {
  const attendees = args.state.attendees;
  const quantity = Math.max(1, Math.min(MAX_REGISTRANT_EDIT_QUANTITY, Math.floor(Number(args.state.quantity || 1))));
  const previousQuantity = Math.max(1, Math.floor(Number(args.previousQuantity || 1)));
  if (!composeFullNameFromEditState(args.state)) return 'Registrant name is required.';
  if (!args.state.email.trim() || !/^\S+@\S+\.\S+$/.test(args.state.email.trim())) {
    return 'Valid email is required.';
  }
  if (attendees.length < 1) return 'At least one attendee is required.';
  if (attendees.length !== quantity) return 'Attendee count must match quantity.';

  for (let index = 0; index < attendees.length; index += 1) {
    const attendee = attendees[index];
    if (!attendee.full_name.trim()) return `Attendee #${index + 1} name is required.`;
    if (attendee.email && !/^\S+@\S+\.\S+$/.test(attendee.email.trim())) {
      return `Attendee #${index + 1} email is invalid.`;
    }
  }

  if (quantity < previousQuantity) {
    for (const checkedInIndex of args.checkedInAttendeeIndexes) {
      if (checkedInIndex >= quantity) {
        return `Cannot remove attendee #${checkedInIndex + 1} because their ticket is already checked in.`;
      }
    }
  }

  for (const field of resolveOrderLevelEditableFields(args.formFields, args.state.orderLevelValues)) {
    const normalized = normalizeCustomFieldSubmissionValue(
      field,
      args.state.orderLevelValues[field.field_key] as CustomFieldInputValue | undefined,
    );
    if (field.is_required && isCustomFieldValueEmpty(field, normalized)) {
      return `Complete required field: ${field.label}.`;
    }
  }

  const perAttendeeFields = resolvePerAttendeeEditableFields(args.formFields, args.state.attendeeResponses);
  for (const field of perAttendeeFields) {
    for (let attendeeIndex = 0; attendeeIndex < attendees.length; attendeeIndex += 1) {
      const responseEntry = args.state.attendeeResponses.find((entry) => entry.attendee_index === attendeeIndex);
      const rawValue = responseEntry?.responses?.[field.field_key] as CustomFieldInputValue | undefined;
      const normalized = normalizeCustomFieldSubmissionValue(
        field,
        rawValue ?? defaultCustomValueForField(field.field_type),
      );
      if (field.is_required && isCustomFieldValueEmpty(field, normalized)) {
        return `Complete required field: ${field.label} for attendee #${attendeeIndex + 1}.`;
      }
    }
  }

  return null;
};

export const isAttendeeSlotCheckedIn = (checkedInAttendeeIndexes: number[], attendeeIndex: number) =>
  checkedInAttendeeIndexes.includes(attendeeIndex);
